/**
 * PolicyGateModal — R148g · Feb 2026 (updated R148l · Feb 14 2026)
 * ---------------------------------------------------------------
 * Client-side gate that blocks the app when the workspace admin has
 * flipped ON "block users until all policies are acknowledged" AND the
 * signed-in user still owes ack(s). Mounted once at AppShell root so it
 * covers every workspace route.
 *
 * R148l · Feb 14 2026 — INLINE READER
 *   The old flow forced users to close the gate to visit /app/policies/{id},
 *   which they couldn't do while the gate was open. Now clicking "Read"
 *   swaps the modal body to an inline reader for that policy so the
 *   employee can read the full body AND acknowledge without ever leaving
 *   the modal. "Back to list" returns to the pending list.
 *
 * We deliberately keep enforcement client-side (per user's choice) —
 * good-faith gate for the common case.
 */
import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { AlertTriangle, CheckCircle2, ScrollText, ArrowLeft, Loader2 } from "lucide-react";
import { pollWhenVisible } from "@/lib/pollWhenVisible";

const CAT_LABEL = {
  hr: "HR", security: "Security", code_of_conduct: "Code of Conduct",
  finance: "Finance", general: "General", it: "IT", compliance: "Compliance",
};

export default function PolicyGateModal() {
  const { activeWorkspaceId, user, impersonation } = useAuth();
  const [state, setState] = useState({ gate_enabled: false, pending: [] });
  const [busyId, setBusyId] = useState(null);
  // Inline reader state — when set, the modal body becomes a
  // read-first-then-acknowledge view for THIS policy.
  const [readerPid, setReaderPid] = useState(null);
  const [readerLoading, setReaderLoading] = useState(false);
  const [reader, setReader] = useState(null);

  const refresh = useCallback(async () => {
    if (!activeWorkspaceId || !user?.id) return;
    try {
      const { data } = await api.get(
        `/workspaces/${activeWorkspaceId}/policies/my-pending`);
      setState(data || { gate_enabled: false, pending: [] });
    } catch { /* silent — a blocked gate check must never crash the shell */ }
  }, [activeWorkspaceId, user?.id]);

  useEffect(() => { refresh(); }, [refresh]);
  // Re-check every 60s so a fresh acknowledgement from an admin push /
  // another tab reflects here quickly. Feb 2026 R281 · Visibility-gated.
  useEffect(() => {
    if (!activeWorkspaceId) return;
    return pollWhenVisible(refresh, 60_000);
  }, [activeWorkspaceId, refresh]);

  const openReader = async (pid) => {
    setReaderPid(pid);
    setReaderLoading(true);
    setReader(null);
    try {
      const { data } = await api.get(`/policies/${pid}`);
      setReader(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Couldn't load the policy")); }
    finally { setReaderLoading(false); }
  };

  const closeReader = () => {
    setReaderPid(null);
    setReader(null);
    setReaderLoading(false);
  };

  const ack = async (pid) => {
    setBusyId(pid);
    try {
      await api.post(`/policies/${pid}/acknowledge`, { signature: user?.name || "" });
      toast.success("Acknowledged");
      // If the reader was open on THIS policy, close it — the pending
      // list refresh may empty out and dismiss the whole gate.
      if (readerPid === pid) closeReader();
      await refresh();
    } catch (e) { toast.error(apiErrorMessage(e, "Ack failed")); }
    finally { setBusyId(null); }
  };

  const open = !impersonation && !!state.gate_enabled && (state.pending || []).length > 0;
  if (!open) return null;

  return (
    <Dialog open modal>
      <DialogContent
        className="max-w-3xl w-[95vw] max-h-[90vh] overflow-y-auto rounded-none"
        hideClose
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        data-testid="policy-gate-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {readerPid ? (
              <>
                <button
                  type="button"
                  onClick={closeReader}
                  className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground border border-border px-2 py-1 rounded-none"
                  data-testid="policy-gate-reader-back"
                >
                  <ArrowLeft className="w-3.5 h-3.5" /> Back to list
                </button>
                <ScrollText className="w-5 h-5 text-primary" />
                <span className="truncate">{reader?.title || "Loading…"}</span>
              </>
            ) : (
              <>
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                Action required · Acknowledge company policies
              </>
            )}
          </DialogTitle>
        </DialogHeader>

        {/* Reader view */}
        {readerPid && (
          <div className="space-y-3 text-sm" data-testid={`policy-gate-reader-${readerPid}`}>
            {readerLoading && (
              <div className="flex items-center gap-2 text-muted-foreground text-xs py-8 justify-center" data-testid="policy-gate-reader-loading">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading policy…
              </div>
            )}
            {!readerLoading && reader && (
              <>
                <div className="flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground border-b border-border pb-2">
                  <span className="chip chip-pending text-[10px]">
                    {CAT_LABEL[reader.category] || reader.category}
                  </span>
                  <span>v{reader.version}</span>
                  {reader.updated_at && (
                    <span>· updated {new Date(reader.updated_at).toLocaleDateString()}</span>
                  )}
                  {reader.effective_from && (
                    <span>· effective {String(reader.effective_from).slice(0, 10)}</span>
                  )}
                </div>
                {reader.summary && (
                  <div className="border-l-2 border-primary/60 pl-3 text-foreground/85 italic text-[13px]">
                    {reader.summary}
                  </div>
                )}
                {/* Rich-text body — policies are stored as sanitized HTML.
                    We render with dangerouslySetInnerHTML because the
                    server strips scripts / iframes before persisting. */}
                <div
                  className="prose prose-sm max-w-none border border-border bg-muted/20 p-4 whitespace-pre-wrap"
                  data-testid="policy-gate-reader-body"
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{ __html: reader.body || reader.body_html || "<em>No policy body.</em>" }}
                />
                <div className="flex items-center justify-between gap-2 sticky bottom-0 bg-background pt-3 border-t border-border">
                  <button
                    type="button"
                    onClick={closeReader}
                    className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                    data-testid="policy-gate-reader-cancel"
                  >
                    <ArrowLeft className="w-3 h-3" /> Back
                  </button>
                  <Button
                    size="sm"
                    className="rounded-none h-9 text-xs px-4"
                    onClick={() => ack(readerPid)}
                    disabled={busyId === readerPid}
                    data-testid={`policy-gate-reader-ack-${readerPid}`}
                  >
                    {busyId === readerPid
                      ? "Recording…"
                      : (<><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> I have read &amp; acknowledge</>)}
                  </Button>
                </div>
              </>
            )}
          </div>
        )}

        {/* List view */}
        {!readerPid && (
          <div className="space-y-3 text-sm">
            <p className="text-muted-foreground">
              Your workspace admin has required every team member to acknowledge
              the policies below before continuing to use the app.
              <b> {state.pending.length}</b> pending.
            </p>
            <ul className="border border-border divide-y divide-border" data-testid="policy-gate-list">
              {state.pending.map((p) => (
                <li key={p.policy_id} className="p-3 flex items-center gap-3" data-testid={`policy-gate-row-${p.policy_id}`}>
                  <ScrollText className="w-4 h-4 text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{p.title}</div>
                    <div className="text-[10px] text-muted-foreground">
                      {CAT_LABEL[p.category] || p.category} · v{p.version}
                      {p.updated_at ? ` · updated ${new Date(p.updated_at).toLocaleDateString()}` : ""}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => openReader(p.policy_id)}
                    className="text-[10px] text-primary hover:underline inline-flex items-center gap-1 border border-primary/40 px-2 py-1"
                    data-testid={`policy-gate-read-${p.policy_id}`}
                    title="Read this policy inside this window"
                  >
                    <ScrollText className="w-3 h-3" /> Read
                  </button>
                  <Button
                    size="sm"
                    className="rounded-none h-8 text-xs"
                    onClick={() => ack(p.policy_id)}
                    disabled={busyId === p.policy_id}
                    data-testid={`policy-gate-ack-${p.policy_id}`}
                  >
                    {busyId === p.policy_id
                      ? "Recording…"
                      : (<><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Acknowledge</>)}
                  </Button>
                </li>
              ))}
            </ul>
            <div className="text-[11px] text-muted-foreground">
              Prefer to read the full text first? Click <b>Read</b> to open the
              policy inside this window and acknowledge in one step.
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
